/* eslint-disable react/no-array-index-key */
'use client';

import { VariantProps } from 'class-variance-authority';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import Button from 'molecules/Button';
import { bodyStyles, headingMoleculeContainer, headingStyles } from 'molecules/Heading/styles';
import Icon from 'molecules/Icon';
import RichText from 'molecules/RichText';
import { hasRichText } from 'molecules/RichText/utils';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import { getResultContent } from 'components/BuildVsBuyQuiz/utils/scoring';

import { hasArrayValues } from 'utils/array/arrays';
import HeadingHighlights from 'utils/strings/HeadingHighlights';

import type { QuizVariant, ResultBucket } from 'components/BuildVsBuyQuiz/config/types';
import type { ResultContentProps } from 'components/BuildVsBuyQuiz/utils/scoring';
import type { ButtonConfigProps } from 'molecules/Button/config';
import type { MoleculeHeadingConfigProps } from 'molecules/Heading/config';
import type { FC, ReactNode } from 'react';

const BuildVsBuyQuizResultsFormWrapper = dynamic(
  () => import('components/BuildVsBuyQuiz/Wrapper/BuildVsBuyQuizResultsFormWrapper'),
);

interface SecondaryCta {
  text?: string;
  url?: string;
  openInNewTab?: boolean;
}

interface HeadingMoleculeProps
  extends
    MergePrefer<VariantProps<typeof headingMoleculeContainer>, MoleculeHeadingConfigProps>,
    VariantProps<typeof headingStyles> {
  children?: ReactNode;
  afterBodySlot?: ReactNode;
  eventDate?: string;
  eventTime?: string;
  afterDates?: ReactNode;
  isForm?: boolean;
  isQuizResultsHeading?: boolean;
  secondaryCta?: SecondaryCta;
  animateOnMount?: boolean;
}

type SidePadding = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'default';

const paddingClass = (p: SidePadding) => {
  switch (p) {
    case 'default':
      return 'px-4 sm:px-16 lg:px-8';
    case 'none':
      return 'px-0';
    case 'sm':
      return 'px-0 md:px-12 lg:px-24 xl:px-32';
    case 'md':
      return 'px-0 md:px-16 lg:px-32 xl:px-40';
    case 'lg':
      return 'px-0 md:px-20 lg:px-40 xl:px-48';
    case 'xl':
      return 'px-0 md:px-24 lg:px-56 xl:px-64';
    default:
      return 'px-0';
  }
};

export const hasHeading = (props: HeadingMoleculeProps) =>
  !!props.eyebrow || hasArrayValues(props.eyebrowWords) || !!props.heading || hasRichText(props.body);

/**
 * Load quiz result content from localStorage
 */
const loadSavedContent = (): ResultContentProps | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = localStorage.getItem('build-vs-buy-quiz-result-content');
    if (!stored) {
      return null;
    }

    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to parse quiz result content from localStorage:', error);

    return null;
  }
};

const Heading: FC<HeadingMoleculeProps> = ({
  as,
  eyebrow,
  eyebrowWords,
  heading: headingProp,
  headingIcon,
  subheading,
  body: bodyProp,
  alignment,
  buttons,
  size,
  children,
  afterBodySlot,
  eventDate,
  eventTime,
  afterDates: afterDatesProp,
  sidePadding,
  isForm = false,
  isQuizResultsHeading = false,
  secondaryCta,
  animateOnMount = false,
}) => {
  const searchParams = useSearchParams();
  const HeadingElem = as || 'h2';
  const sidePaddingValue = sidePadding || 'none';

  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    const handleFormSubmit = () => {
      setFormSubmitted(true);
    };

    window.addEventListener('build-vs-buy-quiz-form-submitted', handleFormSubmit);

    return () => {
      window.removeEventListener('build-vs-buy-quiz-form-submitted', handleFormSubmit);
    };
  }, []);

  const quizResult = searchParams?.get('result') as ResultBucket | null;
  const quizVariant = searchParams?.get('variant') as QuizVariant | null;
  const isQuizResultsPage = Boolean(quizResult);

  let heading = headingProp;
  let body = bodyProp;
  let afterDates = afterDatesProp;
  let resultButtons: ButtonConfigProps[] | undefined;

  if (isQuizResultsPage && quizResult && isQuizResultsHeading) {
    const savedContent = loadSavedContent();
    const resultContent = getResultContent(quizResult, savedContent || undefined);

    heading = resultContent.heading;
    body = resultContent.description;
    resultButtons = resultContent.buttons;

    // CRITICAL FIX: Render wrapper for BOTH variants
    // - gated_start: renders hidden Form 637 auto-submit (always)
    // - gated_end: renders visible Form 640 (only before submission)
    if (!afterDatesProp) {
      if (quizVariant === 'gated_start') {
        // Always render for gated_start - needed to load hidden form
        afterDates = <BuildVsBuyQuizResultsFormWrapper />;
      } else if (quizVariant === 'gated_end' && !formSubmitted) {
        // Render for gated_end only before form submission
        afterDates = <BuildVsBuyQuizResultsFormWrapper />;
      }
    }
  }

  // eyebrowWords (list with colors) takes priority over eyebrow (plain string)
  const hasEyebrowWords = hasArrayValues(eyebrowWords);
  const hasEyebrow = hasEyebrowWords || Boolean(eyebrow);
  const hasHeadingText = Boolean(heading);
  const hasSubheading = Boolean(subheading);
  const hasBody = hasRichText(body);
  const hasButtons = hasArrayValues(buttons);
  const hasChildren = Boolean(children);
  const hasEventInfo = Boolean(eventDate || eventTime);
  const hasAfterDates = Boolean(afterDates);
  const hasSecondaryCta = Boolean(secondaryCta?.text && secondaryCta?.url);

  // Show result buttons only for quiz results heading
  const shouldShowResultButtons =
    isQuizResultsHeading &&
    isQuizResultsPage &&
    resultButtons &&
    resultButtons.length > 0 &&
    (quizVariant === 'gated_start' || (quizVariant === 'gated_end' && formSubmitted));

  const shouldShowHeadingButtons = hasButtons && (!isQuizResultsPage || !isQuizResultsHeading);

  const getButtonSpacing = () => {
    if (isForm) {
      return 'mt-3 space-y-1';
    }
    if (alignment === 'leftInline') {
      return 'mt-6 space-y-3';
    }

    return 'mt-7 space-y-3';
  };

  return (
    <div className={`${headingMoleculeContainer({ alignment, size, isForm })} ${paddingClass(sidePaddingValue)}`}>
      {hasEyebrow && (
        <ScrollFadeSection delay={0.1} animateOnMount={animateOnMount}>
          <span className="font-spaceGrotesk font-semibold text-eyebrow text-overline-sm dark:text-primary-300">
            {hasEyebrowWords
              ? eyebrowWords!.map((word, i) => (
                  <span key={i} style={word.color ? { color: word.color } : undefined}>
                    {i > 0 && ' '}
                    {word.text}
                  </span>
                ))
              : eyebrow}
          </span>
        </ScrollFadeSection>
      )}

      {hasHeadingText && (
        // The gradient (text-heading-gradient = background-clip:text) must live only
        // on the inner element that holds the text and animates. When this h1 wrapper
        // also carries it, the wrapper clips its gradient to the descendant text at
        // the untransformed position while the inner div paints it at the translateY
        // position, so during the mount animation the two layers separate into a ghost
        // double-image. Drop the wrapper's gradient background inline (beats the class,
        // no dark-mode gradient to clip -> no ghost); the inner div keeps the gradient.
        // eslint-disable-next-line no-inline-styles/no-inline-styles
        <HeadingElem className={headingStyles({ size, isForm })} style={{ backgroundImage: 'none' }}>
          <ScrollFadeSection
            delay={0.15}
            animateOnMount={animateOnMount}
            containPaint={false}
            className={headingStyles({ size, isForm })}
          >
            <HeadingHighlights heading={heading || ''} defaultIcon={headingIcon} />
          </ScrollFadeSection>
        </HeadingElem>
      )}

      {hasSubheading && (
        <div className="hidden sm:block">
          <ScrollFadeSection delay={0.18} animateOnMount={animateOnMount}>
            <p className="mt-2 text-display-sm text-white dark:text-gray-300 ">
              <HeadingHighlights heading={subheading || ''} />
            </p>
          </ScrollFadeSection>
        </div>
      )}

      {hasEventInfo && (
        <ScrollFadeSection delay={0.2} animateOnMount={animateOnMount}>
          <div className="flex w-full items-start justify-start gap-3">
            {eventDate && (
              <div className="flex items-center gap-3 text-gray-300">
                <Icon icon="calendar" />
                <span className="text-md">{eventDate}</span>
              </div>
            )}
            {eventTime && (
              <div className="flex items-center gap-3 text-gray-300">
                <Icon icon="clock" />
                <span className="text-md">{eventTime}</span>
              </div>
            )}
          </div>
        </ScrollFadeSection>
      )}

      {hasBody && (
        <ScrollFadeSection delay={0.25} animateOnMount={animateOnMount}>
          <RichText
            text={body || ''}
            className={bodyStyles({ alignment, hasEyebrow, isForm })}
            afterFirstParagraph={afterBodySlot}
            isFormContent={isForm}
            unwrapFirstParagraph={true}
          />
          {alignment === 'leftInline' && shouldShowHeadingButtons && (
            <div className={`w-full sm:flex sm:w-auto sm:gap-3 sm:space-y-0 ${getButtonSpacing()}`}>
              {buttons!.map((button, index) => (
                <Button
                  key={`${button.id}${index}`}
                  {...button}
                  hierarchy={index === 0 ? 'primary' : 'secondary'}
                  className="w-full sm:w-auto"
                />
              ))}
            </div>
          )}
        </ScrollFadeSection>
      )}

      {shouldShowHeadingButtons && alignment !== 'leftInline' && (
        <ScrollFadeSection delay={0.3} animateOnMount={animateOnMount}>
          <div className={`w-full sm:flex sm:w-auto sm:gap-3 sm:space-y-0 ${getButtonSpacing()}`}>
            {buttons!.map((button, index) => (
              <Button
                key={`${button.id}${index}`}
                {...button}
                hierarchy={index === 0 ? 'primary' : 'secondary'}
                className="w-full sm:w-auto"
              />
            ))}
          </div>
        </ScrollFadeSection>
      )}

      {shouldShowResultButtons && (
        <ScrollFadeSection delay={0.3} animateOnMount={animateOnMount}>
          <div className="mt-4 w-full space-y-3 sm:flex sm:w-auto sm:gap-3 sm:space-y-0">
            {resultButtons!.map((button, index) => (
              <Button
                key={`result-button-${index}`}
                {...button}
                hierarchy={index === 0 ? 'primary' : 'secondary'}
                className="w-full sm:w-auto"
              />
            ))}
          </div>
        </ScrollFadeSection>
      )}

      {hasAfterDates && (
        <ScrollFadeSection delay={0.28} animateOnMount={animateOnMount}>
          {afterDates}
        </ScrollFadeSection>
      )}

      {hasSecondaryCta && (
        <ScrollFadeSection
          delay={0.32}
          animateOnMount={animateOnMount}
          className={alignment === 'leftInline' ? 'lg:col-start-2' : undefined}
        >
          <div className="mt-3 flex items-center justify-start gap-1">
            <a
              href={secondaryCta?.url || '#'}
              target={secondaryCta?.openInNewTab ? '_blank' : '_self'}
              rel={secondaryCta?.openInNewTab ? 'noopener noreferrer' : undefined}
              className="group flex items-center gap-1.5 text-lg font-medium text-gray-300 transition-colors hover:text-gray-400"
            >
              <span>{secondaryCta?.text || 'Lorem ipsum'}</span>
              <Icon icon="arrow-up-right" size={20} className="transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </ScrollFadeSection>
      )}

      {hasChildren && (
        <ScrollFadeSection delay={0.35} animateOnMount={animateOnMount}>
          {children}
        </ScrollFadeSection>
      )}
    </div>
  );
};

export default Heading;
